// UI-only tag values used by the dashboard tab/list code to identify shift
// states. These integer codes are not the API's `shifts.state` text values —
// the DB stores `'draft'`, `'available'`, etc. as strings (see SHIFT_STATES
// below). The mapping between the two lives in the components that render
// each section (e.g. the Shifts page draft tab maps DRAFT here to the
// SHIFT_STATES.DRAFT string when filtering API responses).
export const RECENTLY_COMPLETED = 0,
  OPEN = 1,
  FILLED = 2,
  COMPLETED = 3,
  CANCELLED = 4,
  FINISHED = 11,
  IN_PROGRESS = 9,
  EXPIRED = 10,
  DISPUTED = 12,
  DRAFT = 13,
  UNAVAILABLE_TO_WORK = 5,
  AVAILABLE_TO_WORK = 6;
export const DEFAULT_ORG = "0"; // Default organisation ID for "All" organisations
export const DEFAULT_CENTRE = "0"; // Default centre ID for "All" centres
export const bonusList = ["0", "20", "30", "40", "50", "75", "100"];
export const SHIFT_STATES = {
  DRAFT: "draft",
  AVAILABLE: "available",
  EXPIRED: "expired",
  FILLED: "filled",
  CANCELLED: "cancelled",
  CANCELLATE: "cancelled_late",
  IN_PROGRESS: "in_progress",
  NO_SHOW: "no_show",
  FINISHED: "finished",
  COMPLETED: "completed",
  DISPUTED: "disputed",
};
export const SHIFT_BREAK_STATES = {
  SKIPPED: "skip",
  TAKEN: "taken",
  INTERRUPTED_NO_BREAK: "interrupted_no_break",
  INTERRUPTED_HAS_BREAK: "interrupted_has_break",
};

export const APPROVAL_STATUSES = {
  NOT_REQUIRED: "not_required",
  PENDING: "pending",
  APPROVED: "approved",
  REJECTED: "rejected",
};

export const APPROVAL_TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

// Mirrors REJECTION_REASON_MAX_LEN in api/utils/shift_service.js. The server
// enforces this length cap; the client uses it to warn before the request.
export const REJECTION_REASON_MAX_LEN = 1000;

// Human-readable labels for the draft approval states. Shared between the
// shift list pill (Title Case) and the shift detail badge (UPPERCASE via
// toUpperCase()), so the wording stays in sync across both surfaces.
export const APPROVAL_STATUS_LABELS = {
  [APPROVAL_STATUSES.PENDING]: 'Pending Approval',
  [APPROVAL_STATUSES.REJECTED]: 'Rejected',
  [APPROVAL_STATUSES.APPROVED]: 'Approved',
};


export const baseUrl = "/file-content?filePath=";
export const SHIFT_TABS = {
  OPEN: 0,
  FILLED: 1,
  FINISHED: 2,
  COMPLETED: 3,
  CANCELLED: 4,
  EXPIRED: 5,
  DISPUTED: 6,
  DRAFT: 7,
};

export const availableFilter = [
  { id: 1, name: "Available to work" },
  { id: 2, name: "Unavailable to work" },
];

export const documentStatusFilter = [
  { id: "", name: "All" },
  { id: "success", name: "Verified" },
  { id: "verifying", name: "Verifying" },
  { id: "manual_required", name: "Manual required" },
  { id: "rejected", name: "Rejected" },
  { id: "failed", name: "Failed" },
];
export const qualificationFilter = [
  { id: "", name: "All" },
  { id: 0, name: "Unqualified" },
  { id: 1, name: "Certificate III" },
  { id: 3, name: "Diploma" },
  { id: 5, name: "ECT" },
];
// Ordering reflects the access hierarchy (most → least privileged) so the
// role dropdown reads top-to-bottom in a way that matches the mental model
// of "elevation". Support sits last because post-QC-2689 it has the
// narrowest read-only access surface.
//
// IMPORTANT: callers MUST pass each value through labelForRole() (from
// utils/roleLabel) when rendering — these are raw enum strings, and a few
// (centre_director, co_only) would render with a literal underscore under
// CSS `text-transform: capitalize`. The Logins modal does this correctly
// via labelForRole in the MenuItem children.
//
// Note: 'co_only' is an existing user_organisations.access enum value but
// is deliberately NOT in this list — it's a back-office classification, not
// a user-assignable role in the Logins UI. If a future story exposes it,
// add it here in the appropriate hierarchy slot AND ensure labelForRole
// already maps it (it does — "Co Only").
export const permissionList = ["owner", "manager", "centre_director", "support"];

// Provider-side role groups used for client-side UI gating. Keep in sync with
// the server-side PROVIDER_*_ROLES constants in api/auth/authJwt.js — the
// server is the source of truth (these only decide what to render).
//   PROVIDER_WRITE_ROLES — owner + manager + centre_director. Use to gate
//     write/employer-action UI (post shifts, edit, invite, blacklist, etc).
//   PROVIDER_READ_ROLES  — PROVIDER_WRITE_ROLES + support. Use for read-only
//     surfaces (lists, viewers).
export const PROVIDER_WRITE_ROLES = Object.freeze(["owner", "manager", "centre_director"]);
export const PROVIDER_READ_ROLES = Object.freeze([...PROVIDER_WRITE_ROLES, "support"]);

export const LOADING = 0;
export const ERROR = 1;

export const unreadMessageFilter = [
  { id: 1, name: "All Shifts" },
  { id: 2, name: "Only unread messages" },
];

export const assignmentGroupType = {
  all: "all",
  quickcare: "quickcare",
  org: "org",
  centre: "centre",
  invidualAssigned: "individual-assigned",
  invidualRejected: "individual-rejected",
  favorite: "favorite",
};

export const educatorStatusFilter = [
  { id: 0, name: "Active", value: "active" },
  { id: 1, name: "Deactivated", value: "deactivated" },
  { id: 2, name: "Deleted", value: "deleted" },
];

export const EDUCATOR_STATUSES = ["active", "deactivated", "deleted"];

export const qualificationLevelId = {
  NONE: -2,
  ANY: -1,
  UNQUALIFIED: 0,
  CERT3: 1,
  CERT_TOWARD: 2,
  DIPLOMA: 3,
  DIPLOMA_TOWARD: 4,
  ECT: 5,
  ECT_TOWARRD: 6,
  OOSH: 12,
  CERT_AND_DIPLOMA: 13,
};

export const AustralianStateEnum = Object.freeze({
  NSW: "NSW",
  VIC: "VIC",
  QLD: "QLD",
  ACT: "ACT",
  SA: "SA",
  WA: "WA",
  TAS: "TAS",
  NT: "NT",
});

export const DOCUMENT_TYPES = Object.freeze({
  QUALIFICATION: "qualification",
  PREVIOUS_QUALIFICATION: "previous_qualification",
  OSHC_QUALIFICATION: "oshc_qualification",
  RESUME: "resume",
  CPR_CERT: "cpr_cert",
  ANAPHYLAXIS_CERT: "anaphylaxis_cert",
  FIRST_AID_CERT: "first_aid_cert",
  TRANSCRIPT: "transcript",
  CHILD_PROTECTION_CERT: "child_protection_cert",
  CHILD_SAFETY_CERT: "child_safety_cert",
  CHILD_SAFETY_ADV: "child_safety_adv",
  CHILD_SAFETY_LEAD: "child_safety_lead",
  CHILD_PROTECTION_TRAINING: "child_protection_training",
  FOOD_HANDLING_CERT: "food_handling_cert",
  FSS_CERT: "fss_cert",
  OTHER: "other",
  VISA_GRANT_IMMI: "visa_grant_immi",
  NPC_CERT: "npc_cert",
  TEACHER_REGISTRATION: "teacher_registration",
  // QC-2929: held on the profile, never sent to Collabra; file on document.document_url.
  CHANGE_OF_NAME: "change_of_name",
  ACECQA_ACCREDITATION: "acecqa_accreditation",
});

// QC-2772: OSHC qualification tiers. Ids match the backend oshcQualificationTier
// map (api/utils/qualification.js) and are stored on user_info.oshc_qualification.
// Two consumers with different shapes, so this lives here rather than in either
// of them: the Document Rules multi-select (`qualifies_for` on an OOSH rule) reads
// {id, name}, and the manual-verify OSHC Tier select needs {value, label} — see
// OSHC_TIER_SELECT_OPTIONS in docFieldsConfig.
export const OSHC_QUALIFICATION_OPTIONS = [
  { id: 1, name: "First Qualified" },
  { id: 2, name: "Second Qualified" },
  { id: 3, name: "Non-Qualified" },
  { id: 4, name: "WT First Qualified" },
  { id: 5, name: "WT Second Qualified" },
];

// `accept` attribute for onboarding-document file inputs. Images + PDF render
// inline; .docx is accepted too but previewed via a server-side PDF rendition
// (the original .docx is download-only). Keep a single source of truth so the
// several upload inputs across the Documents UI don't drift.
export const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
export const DOC_UPLOAD_ACCEPT = `image/*,application/pdf,.docx,${DOCX_MIME}`;

// Employment types (QC-2842) — the single source of truth for the FE. `key`
// matches the user_info.employment_type enum (and the BE EMPLOYMENT_KEYS). The
// column is NOT NULL DEFAULT 'casual', so a profile always has one of these.
// Consumed by the Educator Detail dropdown and the Compliance Employment Type
// filter — declare the list ONCE here so adding a value can't silently miss a
// consumer.
export const EMPLOYMENT_TYPES = [
  { key: "permanent", label: "Permanent" },
  { key: "casual", label: "Casual" },
  { key: "contract", label: "Contract" },
  { key: "agency", label: "Agency" },
];

// NWR Position (QC-2934) — the NQAITS "Position" values, stored verbatim on
// people.nwr_position (Postgres enum people_nwr_position_enum) and emitted as
// is by every NWR export. people.nwr_position_source says whether the value
// was derived automatically ('auto') or pinned on the profile ('manual').
export const NWR_POSITIONS = [
  "Educator",
  "Assistant",
  "Early Childhood Teacher",
  "Co-ordinator",
  "Non-Educator Staff",
  "Student",
  "Volunteer",
];
// The request value that hands the field back to the automatic rule.
export const NWR_POSITION_AUTOMATIC = "auto";
